/**
 * Hausgeld-Sektion im WEG-Property-Tab (Phase 4).
 *
 * - Übersicht Vorschüsse (aggregiert nach Jahr → Detail-Klick → Monatsliste)
 * - Aus Wirtschaftsplan generieren
 * - Manueller Match zu Bank-Transaktion (später)
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { CurrencyEur, CheckCircle, Warning, Clock, ArrowsClockwise, Envelope } from "@phosphor-icons/react";
import { openAuthenticatedPdf } from "@/lib/pdfUtils";

const MONTHS = ["Jan","Feb","Mär","Apr","Mai","Jun","Jul","Aug","Sep","Okt","Nov","Dez"];
const STATUS_META = {
  open:      { label: "Offen",       color: "bg-amber-100 text-amber-800",       icon: Clock },
  partial:   { label: "Teilzahlung", color: "bg-blue-100 text-blue-800",         icon: Warning },
  paid:      { label: "Bezahlt",     color: "bg-emerald-100 text-emerald-800",   icon: CheckCircle },
  overpaid:  { label: "Überzahlt",   color: "bg-purple-100 text-purple-800",     icon: Warning },
  cancelled: { label: "Storniert",   color: "bg-slate-100 text-slate-500",       icon: Warning },
};
const eur = (v) => (Number(v) || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
const contactName = (c) => c ? ([c.first_name, c.last_name].filter(Boolean).join(" ") || c.company || "—") : "—";

export default function HausgeldSection({ property }) {
  const [plans, setPlans] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [gen, setGen] = useState(false);
  const [overdue, setOverdue] = useState({ count: 0, charges: [] });
  const [dunning, setDunning] = useState(null); // {text, level_label, deadline, ...}

  useEffect(() => {
    api.get(`/weg/${property.id}/economic-plans`).then((r) => {
      setPlans(r.data || []);
      // Default-Jahr = neuestes approved
      const approved = (r.data || []).filter((p) => p.status === "approved");
      if (approved.length) setYear(Math.max(...approved.map((p) => p.year)));
    });
  }, [property.id]);

  const load = () => {
    setLoading(true);
    api.get(`/weg/${property.id}/hausgeld?year=${year}`)
      .then((r) => setRows(r.data || []))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [year, property.id]);

  // Überfällige separat laden
  useEffect(() => {
    api.get(`/weg/${property.id}/hausgeld-overdue?days_grace=14`)
      .then((r) => setOverdue(r.data || { count: 0, charges: [] }));
  }, [property.id, rows]);

  const createDunning = async (charge, level = 1) => {
    try {
      const r = await api.post(`/weg/hausgeld/${charge.id}/dunning`, { level, grace_days: 14 });
      setDunning({ ...r.data, charge_id: charge.id });
      toast.success(`${r.data.level_label} erzeugt`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
  };

  const approvedThisYear = plans.find((p) => p.year === year && p.status === "approved");

  const generate = async () => {
    if (!approvedThisYear) return;
    setGen(true);
    try {
      const r = await api.post(`/weg/economic-plans/${approvedThisYear.id}/generate-hausgeld`, {});
      toast.success(`${r.data.created} Vorschüsse angelegt${r.data.updated ? `, ${r.data.updated} aktualisiert` : ""}${r.data.skipped ? `, ${r.data.skipped} übersprungen` : ""}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Generieren");
    } finally {
      setGen(false);
    }
  };

  // Aggregation nach Einheit für Cross-Tabelle
  const perUnit = useMemo(() => {
    const map = {};
    rows.forEach((r) => {
      const key = r.unit_id;
      if (!map[key]) {
        map[key] = {
          unit: r.unit, owner: r.owner,
          months: {}, total_year: 0, total_paid: 0,
        };
      }
      map[key].months[r.month] = r;
      map[key].total_year += Number(r.amount || 0);
      map[key].total_paid += Number(r.paid_amount || 0);
    });
    return Object.values(map).sort((a, b) => (a.unit?.unit_number || "").localeCompare(b.unit?.unit_number || ""));
  }, [rows]);

  const totalYear = perUnit.reduce((s, u) => s + u.total_year, 0);
  const totalPaid = perUnit.reduce((s, u) => s + u.total_paid, 0);

  return (
    <div className="space-y-3" data-testid="hausgeld-section">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
            <CurrencyEur weight="duotone" className="text-emerald-700" /> Hausgeld-Vorschüsse
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Monatliche Vorschüsse pro Einheit, generiert aus dem genehmigten Wirtschaftsplan.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="h-8 w-28 text-xs" data-testid="hausgeld-year-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {plans.map((p) => (
                <SelectItem key={p.id} value={String(p.year)}>
                  {p.year} {p.status === "draft" ? "(Entwurf)" : ""}
                </SelectItem>
              ))}
              {plans.length === 0 && <SelectItem value={String(year)}>{year}</SelectItem>}
            </SelectContent>
          </Select>
          <Button
            size="sm"
            disabled={!approvedThisYear || gen}
            onClick={generate}
            data-testid="hausgeld-generate-btn"
          >
            <ArrowsClockwise size={13} className="mr-1.5" />
            {gen ? "Erzeuge …" : "Aus Wirtschaftsplan erzeugen"}
          </Button>
        </div>
      </div>

      {!approvedThisYear && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
          <Warning size={12} weight="fill" className="inline mr-1" />
          Für {year} ist noch kein genehmigter Wirtschaftsplan vorhanden. Zuerst Wirtschaftsplan anlegen und genehmigen.
        </div>
      )}

      {loading ? (
        <div className="text-sm text-slate-500">Lade …</div>
      ) : perUnit.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 p-6 text-center bg-slate-50 text-sm text-slate-500" data-testid="hausgeld-empty">
          Noch keine Vorschüsse für {year} generiert.
        </div>
      ) : (
        <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="hausgeld-table">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-2 py-2 sticky left-0 bg-slate-50">Einheit</th>
                <th className="text-left px-2 py-2 sticky left-16 bg-slate-50">Eigentümer</th>
                {MONTHS.map((m, i) => (
                  <th key={m} className="text-center px-1.5 py-2 min-w-[50px]" title={`Monat ${i+1}`}>{m}</th>
                ))}
                <th className="text-right px-2 py-2 border-l">Jahr</th>
                <th className="text-right px-2 py-2">Bezahlt</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {perUnit.map((row) => (
                <tr key={row.unit?.id} data-testid={`hausgeld-row-${row.unit?.unit_number}`}>
                  <td className="px-2 py-1.5 font-mono text-xs">{row.unit?.unit_number}</td>
                  <td className="px-2 py-1.5 text-xs text-slate-700 truncate max-w-[120px]">{contactName(row.owner)}</td>
                  {MONTHS.map((_, i) => {
                    const m = i + 1;
                    const cell = row.months[m];
                    if (!cell) return <td key={m} className="text-center px-1.5 py-1.5 text-slate-300">—</td>;
                    const s = STATUS_META[cell.status] || STATUS_META.open;
                    const Icon = s.icon;
                    return (
                      <td
                        key={m}
                        className={`text-center px-1.5 py-1.5 ${cell.status === "paid" ? "bg-emerald-50" : cell.status === "partial" ? "bg-blue-50" : cell.status === "open" ? "bg-amber-50/60" : ""}`}
                        data-testid={`hausgeld-cell-${row.unit?.unit_number}-${m}`}
                        title={`${cell.matching_reference} · Status: ${s.label}`}
                      >
                        <div className="text-xs font-mono font-medium">{Number(cell.amount).toFixed(0)}</div>
                        <Icon size={10} className={`inline mt-0.5 opacity-70`} weight="fill" />
                      </td>
                    );
                  })}
                  <td className="px-2 py-1.5 text-right font-mono text-xs font-semibold border-l">{eur(row.total_year)}</td>
                  <td className={`px-2 py-1.5 text-right font-mono text-xs ${row.total_paid >= row.total_year ? "text-emerald-700 font-semibold" : "text-slate-500"}`}>
                    {eur(row.total_paid)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-slate-50 text-xs">
              <tr>
                <td colSpan={14} className="px-2 py-1.5 font-semibold">Gesamt</td>
                <td className="px-2 py-1.5 text-right font-mono font-semibold border-l">{eur(totalYear)}</td>
                <td className="px-2 py-1.5 text-right font-mono font-semibold">{eur(totalPaid)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {perUnit.length > 0 && (
        <div className="flex items-center gap-4 text-xs text-slate-500">
          <Badge className={STATUS_META.paid.color}>Bezahlt</Badge>
          <Badge className={STATUS_META.partial.color}>Teilzahlung</Badge>
          <Badge className={STATUS_META.open.color}>Offen</Badge>
          <span>· Zahlungs-Referenz-Format: <code className="text-[11px] bg-slate-100 px-1">HG-{"{Einheit}"}-{year}-MM</code></span>
        </div>
      )}

      {/* Mahnwesen */}
      {overdue.count > 0 && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 mt-3" data-testid="hausgeld-overdue-box">
          <div className="text-sm font-semibold text-rose-900 mb-1.5">
            <Warning size={13} weight="fill" className="inline mr-1" />
            Überfällig: {overdue.count} Vorschüsse (Karenz 14 Tage)
          </div>
          <ul className="space-y-1 max-h-48 overflow-y-auto">
            {overdue.charges.slice(0, 10).map((c) => (
              <li key={c.id} className="flex items-center justify-between text-xs bg-white/60 rounded px-2 py-1">
                <div>
                  <span className="font-mono">{c.unit?.unit_number}</span>
                  <span className="text-slate-600 mx-2">· Fällig {c.due_date}</span>
                  <span className="text-rose-800 font-semibold">{Number(c.outstanding).toFixed(2)} €</span>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" className="h-6 text-[11px]" onClick={() => createDunning(c, 1)} data-testid={`dunning-btn-${c.id}`}>
                    <Envelope size={11} className="mr-1" /> Erinnerung
                  </Button>
                  <Button size="sm" variant="ghost" className="h-6 text-[11px]" onClick={() => createDunning(c, 2)}>
                    1. Mahnung
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Mahnbrief-Preview */}
      <Dialog open={!!dunning} onOpenChange={(o) => !o && setDunning(null)}>
        <DialogContent className="max-w-lg" data-testid="dunning-dialog">
          <DialogHeader><DialogTitle>{dunning?.level_label}</DialogTitle></DialogHeader>
          <div className="text-xs text-slate-500 mb-2">
            Ref: <code>{dunning?.reference}</code> · Offen: <b>{Number(dunning?.outstanding).toFixed(2)} €</b> · Zahlungsfrist: <b>{dunning?.deadline}</b>
          </div>
          <pre className="text-xs whitespace-pre-wrap bg-slate-50 border border-slate-200 rounded p-3 max-h-96 overflow-y-auto font-sans">{dunning?.text}</pre>
          <DialogFooter>
            {dunning?.charge_id && (
              <Button variant="outline" data-testid="dunning-pdf-btn"
                onClick={() => openAuthenticatedPdf(`/weg/pdf/dunning/${dunning.charge_id}?level=${dunning.level}`)}>
                PDF öffnen
              </Button>
            )}
            <Button variant="outline" onClick={() => { navigator.clipboard.writeText(dunning?.text || ""); toast.success("Text kopiert"); }}>Text kopieren</Button>
            <Button onClick={() => setDunning(null)}>Schließen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
